import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ColumnMenu from "../components/context-menu/ColumnMenu";
import { DataTableCard } from "../components/m3";
import CircularityTableActions from "./CircularityTableActions";
import { getM3Colors } from "../theme/style/m3Palette";
import AgGrid from "../components/grid/AgGrid";
import { defaultColDef, floatFormater, formatTo2Decimals, leftAlignColDef, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { Circularity } from "../db_access/circularity";
import { calculate_combined_data } from "../functions/CircularityFunctions";
import BuildingProductDropdown from "../pages/Building/components/BuildingProductDropdown";

// `selectedSubcategoryId`/`onSubcategoryChange` are Building Circularity-only (never
// passed by Product/Project). When present, they render BuildingProductDropdown before
// the column-visibility button as a *controlled* component — the selection, the
// processes list, and the WBLCA fetch all live in BuildingCircularity.js (shared with
// the Material Composition and Utility tabs, so switching tabs keeps the same product
// selected and only one fetch happens). `combined_data` here is already scoped to the
// selected product by the parent, so this component needs no Building-specific fetching
// of its own — it behaves exactly as it always did for Product/Project.
const ProjectMCICalculationTable = ({ combined_data, activeTab, tabValue, topic, selectedSubcategoryId = null, onSubcategoryChange = null }) => {
  const { isSaveClicked, setIsSaveVisible, setRefresh, setIsLoading, columnVisibilities, setIsSaveClicked } = useContextProvider();
  const { t } = useTranslation();
  const theme = useTheme();
  const m3 = getM3Colors(theme.palette.mode === "dark");
  const colVisibility_key = "MCI_calculation_table_column_visibility";
  const gridHeaderContainerRef = useRef(null);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [rows, setRows] = useState([]);
  const [modified, setModified] = useState(false);
  const [total, setTotal] = useState({ description: "Total", quantity: 0, mass_MCI_factor: 0, gwp_total: 0, cost_total: 0 });

  useEffect(() => {
    if (activeTab && tabValue && activeTab !== tabValue) {
      setColumnMenuVisible(false);
    }
  }, [activeTab, tabValue]);

  const onCellValueChanged = (params) => {
    const { data, colDef, value } = params;
    if (colDef.field === "cost_per_unit") {
      setRows((prev) => {
        const new_rows = prev.map((r) => {
          if (r.id === data.id) {
            return { ...r, cost_per_unit: value, isModified: true };
          }
          return r;
        });
        const cd = calculate_combined_data(new_rows);
        setTotal((_) => ({
          description: "Total",
          quantity: cd.quantity_sum,
          mass_MCI_factor: cd.mass_MCI_factor_sum,
          gwp_total: cd.gwp_total_sum,
          cost_total: cd.cost_sum,
        }));

        return cd.combined_circularities;
      });
    }
    setModified(true);
  };
  const productMCI = useMemo(() => combined_data.product_mci, [combined_data.product_mci]);
  const percentageProductMCI = useMemo(() => combined_data.percentage_product_mci, [combined_data.percentage_product_mci]);
  const colDefs = useMemo(() => {
    const isHidden = (field) => {
      if (field === "si_no") return false;
      return !columnVisibilities[colVisibility_key]?.includes(field);
    };
    return [
      {
        headerName: t("SI No."),
        valueGetter: (params) => {
          if (params.node.rowPinned === "bottom") return "";
          return params.node.rowIndex + 1;
        },
        width: 80,
        minWidth: 60,
        headerClass: "ag-center-aligned-header",
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        suppressMenu: true,
        sortable: false,
        field: "si_no"
      },
      { headerName: "Description", field: "description", ...defaultColDef, cellStyle: { ...leftAlignColDef.cellStyle, fontWeight: 500 }, flex: 1 },
      { headerName: "LFI", field: "LFI", ...defaultColDef, cellStyle: { ...rightAlignColDef.cellStyle, color: m3.tertiary }, ...floatFormater },
      { headerName: "MCI", field: "MCI", ...defaultColDef, cellStyle: { ...rightAlignColDef.cellStyle, color: m3.tertiary }, ...floatFormater },
      { headerName: "Mass MCI Factor", field: "mass_MCI_factor", ...defaultColDef, cellStyle: { ...rightAlignColDef.cellStyle, color: m3.info }, ...floatFormater },
      // { headerName: "Project MCI", field: "project_MCI", valueGetter: (_) => calculateProductMCI(rows, massSum), ...rightAlignColDef, ...floatFormater },
      { headerName: "Percentage MCI (%)", field: "percentage_mci", ...defaultColDef, ...rightAlignColDef, ...floatFormater },
      { headerName: "Embodied Carbon (kgCO2e)", field: "gwp_total", ...defaultColDef, cellStyle: { ...rightAlignColDef.cellStyle, color: m3.success }, ...floatFormater },
      { headerName: "Cost per unit (CAD)", field: "cost_per_unit", ...defaultColDef, ...rightAlignColDef, editable: (params) => !params.node.rowPinned, ...floatFormater },
      { headerName: "Embodied Carbon per Cost", field: "embodied_carbon_per_cost", ...defaultColDef, ...rightAlignColDef, ...floatFormater },
      { headerName: "Cost Total (CAD)", field: "cost_total", ...defaultColDef, cellStyle: { ...rightAlignColDef.cellStyle, color: m3.warning }, ...floatFormater },
    ].map((col) => ({ ...col, hide: isHidden(col.field) }));
  }, [columnVisibilities, colVisibility_key, rows, t, m3]);
  const defaultCols = colDefs.map((col) => col.field);

  useEffect(() => {
    const mass_sum = combined_data.mass_sum;
    const quantity = combined_data.quantity_sum;
    const mass_MCI_factor = combined_data.mass_MCI_factor_sum;
    const gwp_total = combined_data.gwp_total_sum;
    const cost_total = combined_data.cost_sum;
    setTotal({ description: "Total", quantity, mass_MCI_factor, gwp_total, cost_total });
  }, [combined_data]);
  useEffect(() => {
    setRows([...combined_data.combined_circularities]);
    setModified(false);
  }, [combined_data.combined_circularities]);
  useEffect(() => {
    if (modified) {
      setIsSaveVisible(true);
    }
  }, [modified]);
  useEffect(() => {
    if (isSaveClicked && modified) {
      const body = rows.filter((r) => r.isModified == true).map((r) => ({ id: r.id, cost_per_unit: r.cost_per_unit }));
      Circularity.update_cost_per_unit(body).finally(() => {
        setModified(false);
        setIsLoading(false);
        setIsSaveClicked(false);
        setRefresh((r) => !r);
      });
    }
  }, [isSaveClicked]);
  return (
    <DataTableCard
      title={t("Material Circularity Indicator Calculation")}
      actions={
        <CircularityTableActions
          columnsAnchorRef={gridHeaderContainerRef}
          onToggleColumns={() => setColumnMenuVisible((p) => !p)}
        >
          {onSubcategoryChange && (
            <BuildingProductDropdown value={selectedSubcategoryId} onChange={onSubcategoryChange} showAllProductsOption={false} />
          )}
        </CircularityTableActions>
      }
    >
      <Box sx={{ height: 'auto' }}>
        <AgGrid
          columnDefs={colDefs}
          rowData={rows}
          onCellValueChanged={onCellValueChanged}
          pinnedBottomRowData={[total]}
          headerCheckbox={false}
          domLayout="autoHeight"
          footerLeftContent={
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>{topic === "Project" ? t("Project MCI") : t("Material Circularity Indicator")}:</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}>{formatTo2Decimals(productMCI)}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>{topic === "Project" ? t("% Project MCI") : t("% Material Circularity Indicator")}:</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}>{formatTo2Decimals(percentageProductMCI * 100)}%</Typography>
              </Box>
            </Box>
          }
        />
      </Box>
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
    </DataTableCard>
  );
};
export default ProjectMCICalculationTable;
